import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * ActionMenu - A reusable dropdown menu for row actions
 * 
 * @param {Object} props
 * @param {Array} props.actions - Array of action objects with shape:
 *   { label: string, icon: Component, onClick: function, variant?: 'default'|'destructive', disabled?: boolean }
 * @param {string} props.testId - Test ID prefix for automation
 */
export const ActionMenu = ({ actions, testId, className }) => {
  if (!actions || actions.length === 0) return null;

  // Stop propagation to prevent row click when clicking the menu
  const handleTriggerClick = (e) => {
    e.stopPropagation();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={handleTriggerClick}>
        <Button
          variant="ghost"
          size="sm"
          className={cn("h-8 w-8 p-0", className)}
          data-testid={testId ? `${testId}-menu-trigger` : "action-menu-trigger"}
        >
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {actions.map((action, index) => {
          const Icon = action.icon;
          const isDestructive = action.variant === "destructive";
          
          // Add separator before destructive actions
          const showSeparator = isDestructive && index > 0 && actions[index - 1]?.variant !== "destructive";
          
          return (
            <React.Fragment key={action.label}>
              {showSeparator && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick?.();
                }}
                disabled={action.disabled}
                className={cn(
                  "cursor-pointer",
                  isDestructive && "text-destructive focus:text-destructive focus:bg-destructive/10"
                )}
                data-testid={testId ? `${testId}-${action.label.toLowerCase().replace(/\s+/g, '-')}` : undefined}
              >
                {Icon && <Icon className="mr-2 h-4 w-4" />}
                {action.label}
              </DropdownMenuItem>
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionMenu;
